import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { FlashRepository } from 'repository/flashRepository';
import { useAlert } from 'contexts/AlertContext';
import { useUserInfo } from 'hooks/useAuth';
import {
    AppScreen, Badge, Button, ButtonFooter, PageIntro, Section, SectionHeader,
} from 'components/ds';
import styles from './Flash.module.css';

/**
 * 추진자용 번개 현황/결정 화면
 */
export const FlashStatus = () => {
    const navigate = useNavigate();
    const { flashId } = useParams();
    const { showAlert, showConfirm } = useAlert();
    const { t } = useTranslation();
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [deciding, setDeciding] = useState<string | null>(null);
    // 후보 탭 = 투표(+1표). 라디오는 "내가 투표한 후보" 표시. 최종 결정은 하단 버튼(주최자 전용).
    const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
    const [voting, setVoting] = useState<string | null>(null);
    const { userNo, nickname } = useUserInfo();
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // 진행중 번개 목록에서 해당 flashId 찾기 (active 엔드포인트는 내 진행중 목록 반환)
    const load = useCallback(async () => {
        try {
            const res = await FlashRepository.onGetActive();
            const list: any[] = res?.data?.data || res?.data || [];
            const found = list.find(f => f.flashId === flashId);
            if (found) setData(found);
            else if (!data) {
                // VOTING 상태가 아닌 경우 — 토큰 조회로 fallback (본인이 만든 거면 확정/취소 후에도 볼 수 있게)
                // 하지만 active가 아니면 그냥 홈으로
                navigate('/home', { replace: true });
            }
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flashId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        pollingRef.current = setInterval(() => { load(); }, 10000);
        return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
    }, [load]);

    const decide = async (candidateId: string, placeName: string) => {
        if (!flashId || deciding) return;
        const ok = await showConfirm({
            title: t('flash.status.decideConfirmTitle'),
            message: t('flash.status.decideConfirmMessage', { place: placeName }),
            confirmText: t('flash.status.decideConfirmOk'),
            cancelText: t('flash.status.decideConfirmCancel'),
        });
        if (!ok) return;
        setDeciding(candidateId);
        try {
            const res = await FlashRepository.onPostDecide(flashId, candidateId);
            // axios instance 의 onRequest 가 4xx/5xx 를 throw 안 하고 error.response 를 반환하므로,
            // status 와 meetingId 둘 다 직접 검사해야 함 (안 하면 silent fail)
            const httpStatus = (res as any)?.status;
            const body: any = (res as any)?.data || {};
            const result: any = body?.data || body;
            const isOk = (httpStatus == null || (httpStatus >= 200 && httpStatus < 300)) && result?.meetingId;
            if (isOk) {
                await showAlert({
                    title: t('flash.status.decideSuccessTitle'),
                    message: t('flash.status.decideSuccessMessage'),
                });
                navigate(`/meeting/detail/${result.meetingId}`, { replace: true });
            } else {
                const serverMsg = body?.message || result?.message;
                await showAlert({ message: serverMsg || t('flash.status.decideFailed') });
            }
        } catch (e: any) {
            await showAlert({ message: e?.response?.data?.message || t('flash.status.decideFailed') });
        } finally {
            setDeciding(null);
        }
    };

    // 후보 탭 → 투표(+1표). 로그인 유저는 userNo 기반 안정 토큰으로 upsert (재탭 시 표 이동).
    const handleVote = async (candidateId: string) => {
        if (!data?.shareToken || voting) return;
        setSelectedCandidateId(candidateId);
        setVoting(candidateId);
        try {
            const voterName = (() => {
                try { return decodeURIComponent(nickname || ''); } catch { return nickname || ''; }
            })() || '주최자';
            await FlashRepository.onPostVote(data.shareToken, {
                candidateId,
                voterName,
                voterToken: 'u_' + (userNo || 0),
                voterUserNo: userNo ? Number(userNo) : undefined,
            });
            await load();
        } catch { /* 투표 실패는 조용히 무시 — 폴링으로 곧 동기화 */ }
        finally { setVoting(null); }
    };

    const cancel = async () => {
        if (!flashId) return;
        const ok = await showConfirm({
            title: t('flash.status.cancelConfirmTitle'),
            message: t('flash.status.cancelConfirmMessage'),
            confirmText: t('flash.status.cancelConfirmOk'),
            cancelText: t('flash.status.cancelConfirmCancelBtn'),
        });
        if (!ok) return;
        try {
            const res = await FlashRepository.onPostCancel(flashId);
            const httpStatus = (res as any)?.status;
            const body: any = (res as any)?.data || {};
            const isOk = httpStatus == null || (httpStatus >= 200 && httpStatus < 300);
            if (isOk) {
                navigate('/home', { replace: true });
            } else {
                await showAlert({ message: body?.message || t('flash.status.cancelFailed') });
            }
        } catch (e: any) {
            await showAlert({ message: e?.response?.data?.message || t('flash.status.cancelFailed') });
        }
    };

    if (loading) {
        return (
            <AppScreen title={t('flash.header.status')} bar="sub">
                <p className={styles.state}>{t('flash.status.loadingText')}</p>
            </AppScreen>
        );
    }
    if (!data) return null;

    const safeDecode = (s?: string): string => {
        if (!s) return '';
        try { return decodeURIComponent(s); } catch { return s; }
    };

    const sorted = [...(data.candidates || [])].sort((a: any, b: any) => b.voteCount - a.voteCount);
    const totalVotes = sorted.reduce((s, c: any) => s + (c.voteCount || 0), 0);
    const selectedCandidate = sorted.find((c: any) => c.candidateId === selectedCandidateId);

    return (
        <AppScreen title={t('flash.header.status')} bar="sub" hasFooter>
            <PageIntro
                title={safeDecode(data.title)}
                desc={t('flash.voteSummary', { votes: data.totalVotes || 0, count: sorted.length })}
            />

            {/* 후보 — 누르면 그 후보로 정합니다 */}
            <Section spacing="tight">
                <SectionHeader title={t('flash.candidatePlace')} count={sorted.length} />
                <ul className={styles.slots}>
                    {sorted.map((c: any, idx: number) => {
                        const isSelected = selectedCandidateId === c.candidateId;
                        const isLeader = idx === 0 && c.voteCount > 0;
                        const pct = totalVotes > 0 ? Math.round((c.voteCount / totalVotes) * 100) : 0;
                        return (
                            <li key={c.candidateId}>
                                <button
                                    type="button"
                                    className={isSelected ? `${styles.pick} ${styles.pickOn}` : styles.pick}
                                    onClick={() => handleVote(c.candidateId)}
                                    aria-pressed={isSelected}
                                >
                                    <span className={styles.pickMark} aria-hidden />
                                    <span className={styles.pickBody}>
                                        <span className={styles.slotTop}>
                                            <span className={styles.slotName}>{safeDecode(c.placeName)}</span>
                                            {isLeader && <Badge tone="lime">{t('flash.vote.leading')}</Badge>}
                                        </span>
                                        {c.placeAddress && (
                                            <span className={styles.slotAddr}>{safeDecode(c.placeAddress)}</span>
                                        )}
                                        <span className={styles.bar}>
                                            <span className={styles.barFill} style={{ width: `${pct}%` }} />
                                        </span>
                                        <span className={styles.slotCount}>
                                            {t('flash.vote.voteCount', { count: c.voteCount || 0 })} · {pct}%
                                            {c.voters?.length > 0
                                                ? ` · ${c.voters.slice(0, 5).map(safeDecode).join(', ')}${c.voters.length > 5
                                                    ? ' ' + t('meetingUpcoming.othersCount', { count: c.voters.length - 5 })
                                                    : ''}`
                                                : ''}
                                        </span>
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>

                {data.shareToken && (
                    <div className={styles.addAction}>
                        <Button
                            variant="secondary"
                            size="lg"
                            block
                            onClick={() => navigate(`/flash/add-place/${data.shareToken}`, {
                                state: { returnTo: `/flash/status/${flashId}` },
                            })}
                        >
                            {t('flash.suggestCandidate')}
                        </Button>
                    </div>
                )}
            </Section>

            <ButtonFooter>
                <Button
                    variant="primary"
                    size="lg"
                    block
                    onClick={() => selectedCandidate
                        && decide(selectedCandidate.candidateId, safeDecode(selectedCandidate.placeName))}
                    disabled={!selectedCandidateId || deciding !== null}
                >
                    {deciding
                        ? t('flash.deciding')
                        : selectedCandidate
                            ? t('flash.decideFinal', { name: safeDecode(selectedCandidate.placeName) })
                            : t('flash.selectCandidate')}
                </Button>
                <Button variant="danger" size="lg" block onClick={cancel}>
                    {t('flash.status.cancelBtn')}
                </Button>
            </ButtonFooter>
        </AppScreen>
    );
};
